<script>
import WorldIcon from '../../icons/WorldIcon.vue';
import MagnifyingGlassIcon from '../../icons/MagnifyingGlassIcon.vue';
import AscendingSortIcon from '../../icons/AscendingSortIcon.vue';
import DescendingSortIcon from '../../icons/DescendingSortIcon.vue';
import Notifications from './Notifications.vue';

const API_URL = 'https://api.cleanair.ai/';

export default {
  components: {
    Notifications,
    DescendingSortIcon,
    AscendingSortIcon,
    MagnifyingGlassIcon,
    WorldIcon,
  },

  props: {
    regions: {
      type: Object,
      required: true,
    },
    types: {
      type: Array,
      required: true,
    },
    contact: {
      type: Object,
      required: false,
      default: () => ({
        details: {},
        regions: [],
      }),
    },
  },

  mounted() {
    const map = new FlaMap(map_cfg);

    map.drawOnDomReady('map-container', (map) => {
      map.on('click', (_, state) => {
        this.regionName = map.fetchStateAttr(state, 'name');
        this.search.regionCode = map.fetchStateAttr(state, 'shortname');
        this.search.countryCode = map.fetchStateAttr(state, 'country');

        const details = this.regions[this.search.countryCode.toUpperCase()];

        if (details) {
          this.selectedRegion = {
            country: details.country,
            region: Object.values(details.regions)
              .find((region) => region.code === this.search.regionCode),
          };
        }

        this.fetchIncentives();
      });
    });
  },

  watch: {
    selectedRegion: {
      deep: true,
      handler(newValue) {
        if (newValue.region) {
          this.search.regionCode = newValue.region.code;
          this.regionName = newValue.region.name;
        } else {
          this.search.regionCode = '';
        }
        this.search.countryCode = newValue.country.code;

        this.fetchIncentives();
      },
    },
  },

  data() {
    return {
      loading: false,
      regionName: '',
      incentives: [],

      search: {
        filter: '',
        type: '',
        regionCode: '',
        countryCode: '',
      },

      selectedRegion: {},

      sortBy: {
        column: 'name',
        direction: 'asc',
      },

      error: false,

      notifications: {
        first_name: '',
        last_name: '',
        email: '',
        organization: '',
        regions: [],
      },
    };
  },

  methods: {
    changeSortBy(column) {
      if (!['name', 'region', 'type'].includes(column)) {
        return;
      }

      if (this.sortBy.column === column) {
        this.sortBy.direction = this.sortBy.direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy.direction = 'asc';
      }

      this.sortBy.column = column;
    },

    async fetchIncentives() {
      try {
        this.loading = true;
        this.error = false;

        const { data } = await axios.get(`${API_URL}financial-incentives/v1/${this.search.countryCode}/${this.search.regionCode}`);

        this.incentives = data.data;
      } catch (error) {
        this.error = true;
        console.error('Error fetching data:', error.message);
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    filteredIncentives() {
      const { filter } = this.search;
      const lowercaseFilter = filter.toLowerCase();

      return this.incentives.filter(
        (incentive) => lowercaseFilter === '' || (
          incentive.name.toLowerCase()
            .includes(lowercaseFilter)
        ),
      )
        .sort((a, b) => {
          const result = (a[this.sortBy.column] || '').toLowerCase()
            .localeCompare((b[this.sortBy.column] || '').toLowerCase(), undefined, { sensitivity: 'base' });

          if (this.sortBy.direction === 'desc') {
            return -result;
          }

          return result;
        });
    },
  },
};
</script>

<template>
    <div class="container">
        <div id="map-container" style="margin: auto;"></div>

        <div v-if="loading" class="loading-overlay d-flex">
            <div class="spinner-border text-light" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>

        <div>
            <div class="row">
                <h2 class="col-12 col-md-8">
                    {{ regionName }}<br/>

                    <Notifications :contact="contact" :regions="regions" :types="types"/>
                </h2>
                <div class="col-12 col-md-4 text-right">
                    <form method="post" style="margin-bottom: 4px;" @submit.prevent="">
                        <div class="input-group pb-1">
                            <div class="input-group-prepend">
                                <span class="input-group-text">
                                    <WorldIcon/>
                                </span>
                            </div>
                            <select v-model="selectedRegion" class="form-control">
                                <optgroup v-for="{country, regions} in regions" :label="country.name">
                                    <option v-for="region in regions" :value="{country, region}">
                                        {{ region.name }}
                                    </option>
                                </optgroup>
                            </select>
                        </div>
                        <div class="input-group pb-1">
                            <div class="input-group-prepend">
                                <span class="input-group-text">
                                   Program Type
                                </span>
                            </div>
                            <select v-model="search.type" class="form-control">
                                <option v-for="(type, index) in types" :key="`type-${index}`">
                                    {{ type }}
                                </option>
                            </select>
                        </div>
                        <div class="input-group pb-1">
                            <div class="input-group-prepend">
                                <span class="input-group-text">
                                    <MagnifyingGlassIcon/>
                                </span>
                            </div>
                            <input id="filter" v-model="search.filter" class="form-control" type="text">
                        </div>
                        <div style="clear: both;"></div>
                    </form>
                </div>
            </div>

            <table class="table table-striped table-responsive-sm">
                <thead>
                <tr>
                    <th scope="col" @click="changeSortBy('region')">
                        Region
                        <AscendingSortIcon v-if="sortBy.column === 'region' && sortBy.direction === 'asc'"/>
                        <DescendingSortIcon v-else-if="sortBy.column === 'region'"/>
                    </th>
                    <th scope="col" @click="changeSortBy('name')">
                        Program Name

                        <AscendingSortIcon v-if="sortBy.column === 'name' && sortBy.direction === 'asc'"/>
                        <DescendingSortIcon v-else-if="sortBy.column === 'name'"/>

                    </th>
                    <th scope="col" @click="changeSortBy('type')">
                        Details

                        <AscendingSortIcon v-if="sortBy.column === 'type' && sortBy.direction === 'asc'"/>
                        <DescendingSortIcon v-else-if="sortBy.column === 'type'"/>

                    </th>
                    <th scope="col"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="incentive in filteredIncentives" :key="incentive.id">
                    <td>{{ !incentive.region ? incentive.country : incentive.region }}</td>
                    <td>
                        {{ incentive.name }}
                        <div v-if="!!incentive.description">
                            <small>{{ incentive.description }}</small>
                        </div>

                        <span v-if="!!incentive.expirationDate" class="pt-2 d-block">
                            <strong>Expiration Date</strong>:<br/>
                            {{ incentive.expirationDate }}
                        </span>
                    </td>
                    <td>
                        <ul class="list-unstyled">
                            <li>
                                <strong>Type</strong>:<br/>
                                {{ incentive.type }}
                            </li>
                        </ul>
                    </td>
                    <td>
                        <ul class="list-unstyled">
                            <li v-for="link in incentive.links" :key="link.id">
                                <a :href="link.url" target="_blank">{{ link.name }}</a>
                            </li>
                        </ul>
                    </td>
                </tr>
                </tbody>
                <tfoot v-if="filteredIncentives.length === 0">
                <tr>
                    <td class="text-center" colspan="100%">No results found. Please start by clicking on the map.</td>
                </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<style scoped>
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: none;
    align-items: center;
    justify-content: center;
}

.loading-overlay .spinner-border {
    width: 3rem;
    height: 3rem;
}
</style>
